import {
  EMPLOYEE_FUNCTION_LABELS,
  YARD_STATE_LABELS,
} from "@etudescarre/constants";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  ArrowsClockwiseIcon,
  DownloadSimpleIcon,
  GearSixIcon,
  HardHatIcon,
  LightningIcon,
  ListIcon,
  SignOutIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useParams } from "react-router";
import { getYardState } from "@/features/staff/yard-state";
import { useGuardedNavigate } from "@/lib/utils/unsaved-changes";
import { useYardContext } from "@/providers/yard-context";
import {
  downloadNomenclature,
  downloadsRefusal,
} from "./download-nomenclature";

type YardHeaderProps = {
  onToggleNav: () => void;
  onToggleActions: () => void;
};

export function YardHeader({ onToggleNav, onToggleActions }: YardHeaderProps) {
  const { yard, contracts, primaryContract } = useYardContext();
  const { yardId } = useParams<{ yardId: string }>();
  const navigate = useGuardedNavigate();
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadRefusal = downloadsRefusal(contracts.map(({ type }) => type));
  const yardState = getYardState(yard);

  const handleDownloadNomenclature = async () => {
    if (isNil(yardId)) return;
    setIsDownloading(true);
    try {
      await downloadNomenclature(yardId);
    } finally {
      setIsDownloading(false);
    }
  };

  const functionLabel = isNotNil(primaryContract?.function)
    ? (EMPLOYEE_FUNCTION_LABELS[primaryContract.function] ??
      primaryContract.function)
    : null;

  return (
    <header className="flex items-center justify-between border-b border-zinc-200 bg-white px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onToggleNav}
          className="cursor-pointer rounded-md p-2 text-zinc-600 hover:bg-zinc-100 lg:hidden dark:text-zinc-400 dark:hover:bg-zinc-800"
          aria-label="Ouvrir le menu de navigation"
          title="Menu de navigation"
        >
          <ListIcon className="size-5" />
        </button>

        <div className="flex items-center gap-2">
          <HardHatIcon className="size-5 text-zinc-500" />
          <div>
            <div className="flex items-center gap-1">
              <h1 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                Bienvenue sur {yard.name}
              </h1>
              <button
                type="button"
                onClick={() => navigate(`/yards/${yardId}/settings`)}
                className="cursor-pointer rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
                aria-label="Réglages du chantier"
                title="Réglages du chantier"
              >
                <GearSixIcon className="size-4" />
              </button>
            </div>
            {isNotNil(functionLabel) && (
              <p className="text-xs text-zinc-500">{functionLabel}</p>
            )}
          </div>
        </div>
      </div>

      {(yard.isLocked ||
        yardState === "preparation" ||
        yardState === "cloture") && (
        <div className="rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
          {yardState === "preparation"
            ? "Chantier en préparation"
            : `État : ${YARD_STATE_LABELS[yardState]}`}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleDownloadNomenclature}
          disabled={isNotNil(downloadRefusal) || isDownloading}
          className="cursor-pointer rounded-md p-2 text-zinc-600 enabled:hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-zinc-400 dark:enabled:hover:bg-zinc-800"
          aria-label="Télécharger la nomenclature"
          title={downloadRefusal ?? "Télécharger la nomenclature"}
        >
          <DownloadSimpleIcon className="size-5" />
        </button>

        <button
          type="button"
          onClick={onToggleActions}
          className="cursor-pointer rounded-md p-2 text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
          aria-label="Ouvrir le panneau d'actions"
          title="Actions"
        >
          <LightningIcon className="size-5" />
        </button>

        <button
          type="button"
          onClick={() => navigate("/organizations/select")}
          className="cursor-pointer rounded-md p-2 text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
          aria-label="Changer de chantier"
          title="Changer de chantier"
        >
          <ArrowsClockwiseIcon className="size-5" />
        </button>

        <button
          type="button"
          onClick={() => navigate("/auth/sign-out")}
          className="cursor-pointer rounded-md p-2 text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
          aria-label="Déconnexion"
          title="Déconnexion"
        >
          <SignOutIcon className="size-5" />
        </button>
      </div>
    </header>
  );
}
